import { useState, useEffect, useRef, useCallback } from "react";
import { Loader2, ImageIcon } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";

interface ThumbnailFramePickerProps {
  playbackId: string;
  duration: number;
  currentTime?: number;
  onSelect: (time: number) => void;
  isLoading?: boolean;
}

function formatTimestamp(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function snapToHalf(value: number): number {
  return Math.round(value * 2) / 2;
}

export default function ThumbnailFramePicker({
  playbackId,
  duration,
  currentTime = 0,
  onSelect,
  isLoading = false,
}: ThumbnailFramePickerProps) {
  const [selectedTime, setSelectedTime] = useState(snapToHalf(currentTime));
  const [debouncedTime, setDebouncedTime] = useState(snapToHalf(currentTime));
  const [imageLoading, setImageLoading] = useState(true);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const previewUrl = `https://image.mux.com/${playbackId}/thumbnail.jpg?width=640&height=360&time=${debouncedTime}`;

  const handleSliderChange = useCallback((value: number[]) => {
    const snapped = snapToHalf(value[0]);
    setSelectedTime(snapped);
    setImageLoading(true);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedTime(snapped);
    }, 500);
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <div className="space-y-4">
      {/* Preview image */}
      <div className="relative aspect-video rounded-lg overflow-hidden bg-muted">
        <img
          src={previewUrl}
          alt={`Frame at ${formatTimestamp(selectedTime)}`}
          className="w-full h-full object-cover"
          onLoad={() => setImageLoading(false)}
          onError={() => setImageLoading(false)}
        />
        {imageLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
            <Loader2 className="h-8 w-8 animate-spin text-white" />
          </div>
        )}
      </div>

      {/* Slider */}
      <div className="space-y-2">
        <Slider
          value={[selectedTime]}
          min={0}
          max={duration}
          step={0.5}
          onValueChange={handleSliderChange}
        />
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>{formatTimestamp(selectedTime)}</span>
          <span>{formatTimestamp(duration)}</span>
        </div>
      </div>

      {/* Save button */}
      <Button
        className="w-full"
        onClick={() => onSelect(selectedTime)}
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <ImageIcon className="mr-2 h-4 w-4" />
            Save thumbnail
          </>
        )}
      </Button>
    </div>
  );
}
